@if ((workout$ | async); as w) {
  <!-- Minimized: Spotify-like bar with round clock + time -->
  @if (minimized) {
    <div class="minimized-bar">
      <div class="minimized-expand" (click)="reopen()" role="button" tabindex="0" aria-label="Show player">
        <div class="minimized-clock" [class.warning]="w.phase === 'warning'" [class.urgent]="w.phase === 'urgent'">
          <svg viewBox="0 0 40 40" class="minimized-ring">
            <circle class="bg" cx="20" cy="20" r="18"></circle>
            <circle
              class="fg"
              cx="20"
              cy="20"
              r="18"
              [attr.stroke-dasharray]="circleLength"
              [attr.stroke-dashoffset]="(1 - w.progress) * circleLength"
            ></circle>
            <text x="20" y="20" text-anchor="middle" dominant-baseline="middle" class="time-text" font-size="10">{{ w.remainingMs | formatDuration }}</text>
          </svg>
        </div>
        <span class="minimized-label">{{ w.label }}</span>
        <span class="minimized-time">{{ w.remainingMs | formatDuration }}</span>
        <ion-icon name="chevron-up" aria-hidden="true"></ion-icon>
      </div>
      <div class="minimized-controls">
        <!-- While the step's media loads the workout is starting, not paused: the
             button offers Pause, which cancels the start. -->
        <button type="button" class="mini-btn" [class.is-starting]="w.isStarting" (click)="w.isPaused && !w.isStarting ? (w.isYoutubeVideo ? reopenAndPlay() : resume()) : pause()" [attr.aria-label]="w.isPaused && !w.isStarting ? 'Resume' : 'Pause'" [attr.aria-busy]="w.isStarting">
          <ion-icon [name]="w.isPaused && !w.isStarting ? 'play' : 'pause'" aria-hidden="true"></ion-icon>
        </button>
        <button type="button" class="mini-btn mini-btn-stop" (click)="stop()" aria-label="Stop">
          <ion-icon name="stop-circle" aria-hidden="true"></ion-icon>
        </button>
      </div>
    </div>
  }
  <!-- Full panel — "Workout in Progress" -->
  @if (!minimized) {
    <div class="panel wip" #panelEl (touchstart)="onTouchStart($event)" (touchend)="onTouchEnd($event)" (touchcancel)="onTouchCancel()">
      <div class="wip-top">
        <button type="button" class="wip-back" (click)="minimize()" aria-label="Back"><ion-icon name="chevron-back"></ion-icon></button>
        <div class="wip-title">
          <!-- Compact brand shown only on very short screens where the full brand
               footer below has no room (see .wip-title-brand @media). -->
          <span class="wip-title-brand">@if (brandLogo) {<img [src]="brandLogo" class="wip-title-logo" alt="" />}{{ brandName || appName }}</span>
          <span class="wip-title-text">Workout in Progress</span>
        </div>
        <button type="button" class="wip-stop" data-testid="player-stop" (click)="stop()" aria-label="Stop"><ion-icon name="close"></ion-icon></button>
      </div>
      <!-- Media stage -->
      @if (hasVisualMedia(w)) {
        <div class="wip-media" #mediaStage>
          <!-- Confirms a double-tap (or arrow-key) seek; the gesture has no
               other affordance on the video itself. -->
          @if (seekFlash) {
            <div class="wip-seek-flash" [class.is-back]="seekFlash === 'back'" data-testid="player-seek-flash" aria-hidden="true">
              <ion-icon [name]="seekFlash === 'back' ? 'play-back' : 'play-forward'"></ion-icon>
              <span>{{ seekStepSec }}</span>
            </div>
          }
          @if (w.videoUrl && !w.isYoutubeVideo) {
            <video #runnerVideo [src]="w.videoUrl" [poster]="currentActivityImageUrl || videoBlankPoster" [loop]="!videoCropActive(w)" playsinline [muted]="!!w.muteVideo || !!w.videoSoundNative" disablePictureInPicture class="wip-video" preload="metadata" (error)="onLocalVideoReady(); onVideoError(runnerVideo)" (loadstart)="onLocalVideoWaiting()" (waiting)="onLocalVideoWaiting()" (loadeddata)="onLocalVideoReady(); onLocalVideoLoaded()" (canplay)="onLocalVideoReady()" (loadedmetadata)="onVideoMeta(runnerVideo)" (durationchange)="onVideoDurationChange(runnerVideo)" (playing)="onLocalVideoPlaying()" (timeupdate)="onVideoTime(runnerVideo)"></video>
            <!-- Loading or rebuffering: the sound can already be playing (a native
                 soundtrack), so say the picture is coming. Not over a paused step. -->
            @if (videoLoading && !(w.isPaused && !w.isStarting)) {
              <div class="wip-video-loading" data-testid="player-video-loading" role="status" aria-label="Loading video">
                <ion-spinner name="crescent"></ion-spinner>
              </div>
            }
            <!-- Tap pauses; double-tap left/right seeks ∓10s (see onMediaTap). -->
            <div class="wip-media-tap" (click)="onMediaTap($event, w.isPaused && !w.isStarting)" [attr.aria-label]="w.isPaused && !w.isStarting ? 'Resume' : 'Pause'"></div>
            @if (videoWindowLen(w) > 0) {
              <div class="wip-scrubber">
                <span>{{ (videoWindowCurrent(w) * 1000) | formatDuration }}</span>
                <ion-range min="0" [max]="videoWindowLen(w)" step="0.1" [value]="videoWindowCurrent(w)" (ionChange)="seekVideo($event, runnerVideo)" aria-label="Seek"></ion-range>
                <span>{{ (videoWindowLen(w) * 1000) | formatDuration }}</span>
                <button type="button" class="wip-fs" (click)="toggleFullscreen()" aria-label="Fullscreen"><ion-icon name="scan-outline"></ion-icon></button>
              </div>
            }
          }
          @if (w.videoUrl && w.isYoutubeVideo && ytFrameVisible) {
            <!-- Audio-kind YouTube: the iframe stays resident and playing (only way to get
                 YouTube sound) but is hidden behind a square zoomed thumbnail. -->
            <iframe #youtubeFrame [src]="youtubeSafeUrl" (load)="onYtIframeLoad()" class="wip-video wip-iframe" [class.wip-iframe--audio]="w.isYoutubeAudio" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowfullscreen></iframe>
            @if (w.isYoutubeAudio && youtubeAudioCoverUrl(w)) {
              <img [src]="youtubeAudioCoverUrl(w)" class="wip-cover wip-cover--overlay" alt="" data-testid="player-yt-audio-cover" />
            }
            <div class="wip-media-tap" (click)="onMediaTap($event, w.isPaused && !w.isStarting)"></div>
            @if (ytDurationSec > 0) {
              <div class="wip-scrubber">
                <span>{{ (ytCurrentSec * 1000) | formatDuration }}</span>
                <ion-range min="0" [max]="ytDurationSec" step="0.1" [value]="ytCurrentSec" (ionChange)="seekYoutube($event)" aria-label="Seek"></ion-range>
                <span>{{ (ytDurationSec * 1000) | formatDuration }}</span>
                @if (!w.isYoutubeAudio) {
                  <button type="button" class="wip-fs" (click)="toggleFullscreen()" aria-label="Fullscreen"><ion-icon name="scan-outline"></ion-icon></button>
                }
              </div>
            }
          }
          @if (!w.videoUrl && currentActivityImageUrl) {
            <img [src]="currentActivityImageUrl" class="wip-cover" alt="" data-testid="player-cover" />
          }
          <!-- Media the exercise names but this device does not hold. Nothing here
               blocks the workout — the timer runs on regardless — so every option
               is a way forward rather than a wall. -->
          @if (showMissingMedia(w)) {
            <div class="wip-missing" data-testid="player-media-missing">
              @if (!missingMediaGone) {
                <p class="wip-missing-title">{{ MissingMedia.Title }}</p>
                <p class="wip-missing-body">{{ MissingMedia.Body }}</p>
                <div class="wip-missing-actions">
                  <ion-button size="small" fill="solid" [disabled]="missingMediaBusy" (click)="downloadMissingMedia(w.activityId)" data-testid="player-media-download">
                    @if (missingMediaBusy) { <ion-spinner name="crescent" slot="start"></ion-spinner> }
                    {{ missingMediaBusy ? MissingMedia.Downloading : MissingMedia.Download }}
                  </ion-button>
                  <ion-button size="small" fill="clear" (click)="dismissMissingMedia(w.activityId)" data-testid="player-media-dismiss">
                    {{ MissingMedia.Dismiss }}
                  </ion-button>
                </div>
              } @else {
                <!-- A download has proved the cloud has not got it either, so the
                     only thing left to offer is removing the dead reference. -->
                <p class="wip-missing-title">{{ MissingMedia.GoneTitle }}</p>
                <p class="wip-missing-body">{{ MissingMedia.GoneBody }}</p>
                <div class="wip-missing-actions">
                  <ion-button size="small" fill="solid" color="danger" [disabled]="missingMediaBusy" (click)="removeMissingMedia(w.activityId)" data-testid="player-media-remove">
                    {{ MissingMedia.Remove }}
                  </ion-button>
                  <ion-button size="small" fill="clear" (click)="dismissMissingMedia(w.activityId)" data-testid="player-media-keep">
                    {{ MissingMedia.Dismiss }}
                  </ion-button>
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- Stat cards: whole-workout remaining vs current-activity remaining -->
      <div class="wip-cards">
        <div class="wip-card">
          <div class="wip-card-label">Duration</div>
          <div class="wip-card-value" data-testid="player-duration">{{ w.workoutRemainingMs | formatDuration }}</div>
        </div>
        <div class="wip-card wip-card--accent" [class.warning]="w.phase==='warning'" [class.urgent]="w.phase==='urgent'">
          <div class="wip-card-label">Remaining</div>
          <div class="wip-card-value" data-testid="player-remaining">{{ w.activityRemainingMs | formatDuration }}</div>
        </div>
      </div>
      <!-- Focus: activity name + activity#/total + set badge (+ breathing phase / cadence) -->
      <div class="wip-focus">
        <div class="wip-activity-name" data-testid="player-activity-title">{{ w.label }}</div>
        <!-- Two columns: the big activity counter on the left, the set/rep/duration
             stack on the right — collapses what were 3 stacked rows into one band. -->
        <div class="wip-focus-stats">
          <div class="wip-counter"><span class="cur">{{ w.activityNumber }}</span><span class="tot"> / {{ w.activityTotal }}</span></div>
          @if (w.setCount > 1 || w.repCount > 1) {
            <div class="wip-focus-meta">
              @if (w.setCount > 1) {
                <span class="wip-set-badge">TARGET SET {{ w.setIndex }} / {{ w.setCount }}</span>
              }
              @if (w.repCount > 1) {
                <span class="wip-rep-badge">REP {{ w.repIndex }} / {{ w.repCount }}</span>
              }
              @if (w.unitDurationSec > 0) {
                <span class="wip-timing">{{ w.unitDurationSec * 1000 | formatDuration }} each · {{ w.setCount * w.repCount * w.unitDurationSec * 1000 | formatDuration }} total</span>
              }
            </div>
          }
        </div>
        @if (w.cadenceBpm) {
          <div class="wip-cadence muted" data-testid="player-bpm"><ion-icon name="speedometer-outline" [class.pulse]="!w.isPaused" aria-hidden="true"></ion-icon> {{ w.cadenceBpm }} BPM</div>
        }
      </div>
      <!-- Audio scrubber (when audio plays) -->
      @if (w.hasAudio) {
        <div class="wip-audio">
          <ion-icon name="musical-notes" [class.pulse]="!w.isPaused" aria-hidden="true"></ion-icon>
          <ion-range min="0" max="1" step="0.001" [value]="w.audioProgress" (ionChange)="onSeekAudio($event)" aria-label="Seek audio"></ion-range>
          @if (w.isRepeat) {
            <ion-icon name="repeat" aria-hidden="true"></ion-icon>
          }
        </div>
      }
      <!-- Up next -->
      @if (nextActivity; as n) {
        <div class="wip-next surface">
          <div class="wip-next-thumb">
            @if (nextImageUrl) {
              <img [src]="nextImageUrl" alt="" />
            }
            @if (!nextImageUrl) {
              <ion-icon name="barbell-outline" aria-hidden="true"></ion-icon>
            }
          </div>
          <div class="wip-next-info">
            <div class="wip-next-label">Up next</div>
            <div class="wip-next-name">{{ n.name }}</div>
            <div class="wip-next-sub muted">{{ n.summary }}</div>
          </div>
          <button type="button" class="wip-next-skip" (click)="skipToNext()" aria-label="Skip to next"><ion-icon name="play-skip-forward"></ion-icon></button>
        </div>
      }
      <!-- Brand footer: the app's own brand as a watermark filling the space above
           the transport. On very short screens it is hidden (see @media) and the
           compact brand in the header takes over instead. -->
      @if (brandingReady) {
      <div class="wip-brand">
        <!-- Left column: sized logo with the brand name beneath it. -->
        <div class="wip-brand-left">
          @if (brandLogo) {
            <img [src]="brandLogo" class="wip-brand-logo" alt="" />
          }
          @if (brandName) {
            <div class="wip-brand-name">{{ brandName }}</div>
          }
        </div>
        <!-- Right column: tagline over the contact details. -->
        <div class="wip-brand-right">
          @if (brandTagline) {
            <div class="wip-brand-tagline">{{ brandTagline }}</div>
          }
          @if (brandWebsite || brandInstagram || brandContactEmail) {
            <div class="wip-brand-meta">
              @if (brandWebsite) {
                <span class="wip-brand-item"><ion-icon name="globe-outline" aria-hidden="true"></ion-icon><span class="wip-brand-text">{{ brandWebsite }}</span></span>
              }
              @if (brandInstagram) {
                <span class="wip-brand-item"><ion-icon name="logo-instagram" aria-hidden="true"></ion-icon><span class="wip-brand-text">{{ brandInstagram }}</span></span>
              }
              @if (brandContactEmail) {
                <span class="wip-brand-item wip-brand-item--email"><ion-icon name="mail-outline" aria-hidden="true"></ion-icon><span class="wip-brand-text">{{ brandContactEmail }}</span></span>
              }
            </div>
          }
        </div>
      </div>
      }
      <!-- Transport: ⏮ · −10s · ⏯ · +10s · ⏭ -->
      <div class="wip-controls">
        <button type="button" class="wip-ctrl" data-testid="player-skip-prev" (click)="skipToPrev()" [disabled]="!w.canGoBack" aria-label="Previous"><ion-icon name="play-skip-back"></ion-icon></button>
        <button type="button" class="wip-ctrl wip-ctrl--nudge" data-testid="player-nudge-back" (click)="nudge(-10)" aria-label="Back 10 seconds"><ion-icon name="play-back"></ion-icon><span>10</span></button>
        <button type="button" class="wip-ctrl wip-ctrl--play" data-testid="player-play" [class.is-starting]="w.isStarting" (click)="w.isPaused && !w.isStarting ? resume() : pause()" [attr.aria-label]="w.isPaused && !w.isStarting ? 'Resume' : 'Pause'" [attr.aria-busy]="w.isStarting"><ion-icon [name]="w.isPaused && !w.isStarting ? 'play' : 'pause'"></ion-icon></button>
        <button type="button" class="wip-ctrl wip-ctrl--nudge" data-testid="player-nudge-fwd" (click)="nudge(10)" aria-label="Forward 10 seconds"><ion-icon name="play-forward"></ion-icon><span>10</span></button>
        <button type="button" class="wip-ctrl" data-testid="player-skip-next" (click)="skipToNext()" [disabled]="!w.activityQueue?.length" aria-label="Next"><ion-icon name="play-skip-forward"></ion-icon></button>
      </div>
    </div>
  }
}
